<template>
  <div class="mensagens-programadas-lote-page">
    <div class="page-header">
      <Button
        :label="t('Nova Mensagem Programada em Lote')"
        icon="pi pi-calendar-plus"
        severity="primary"
        data-testid="mensagens-programadas-lote-new-btn"
        @click="openModal"
      />
    </div>

    <MensagensProgramadasLoteTable
      :translations="translations"
      @edit="handleEdit"
      @data-loaded="handleTableDataLoaded"
    />

    <!-- Progress indicators for active bulk operations -->
    <div v-if="activeLotes.length > 0" class="active-progress-section">
      <h4 class="active-progress-title">{{ t('Progresso das Operações Ativas') }}</h4>
      <div
        v-for="lote in activeLotes"
        :key="lote.ID"
        class="active-progress-item"
      >
        <span class="active-progress-label">{{ t('Lote') }} #{{ lote.ID }}</span>
        <MensagensProgramadasLoteProgress :lote-id="lote.ID" />
      </div>
    </div>

    <!-- MensagensProgramadasLoteModal is centralized in ModalManager.vue and opened via eventBus -->
    <!-- MessageTemplateSelectionModal is centralized in ModalManager.vue and opened via eventBus -->
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, provide } from 'vue';
import { useI18n } from 'vue-i18n';
import MensagensProgramadasLoteTable from './MensagensProgramadasLoteTable.vue';
import MensagensProgramadasLoteProgress from './MensagensProgramadasLoteProgress.vue';
import { eventBus } from '@/eventBus';
import { log } from '@/utils/DebugLogger';

type Id = number | string;

interface LoteRow {
  ID: Id;
  status?: string;
  status_label?: string;
}

interface Props {
  translations?: Record<string, string>;
}

const props = withDefaults(defineProps<Props>(), {
  translations: () => ({}),
});

const { t } = useI18n();

// Provide translations to child components
provide('translations', props.translations);

// Data
const activeLotes = ref<LoteRow[]>([]);

// Methods
const handleTableDataLoaded = (data: LoteRow[] | null | undefined): void => {
  log('MensagensProgramadasLotePage', 'handleTableDataLoaded', 'Table data loaded, finding active lotes');
  activeLotes.value = (data || []).filter(lote => {
    const status = (lote.status || '').toLowerCase();
    return status === 'processing' || status === 'pending' ||
      lote.status_label === 'Processando' || lote.status_label === 'Pendente';
  });
  log('MensagensProgramadasLotePage', 'handleTableDataLoaded', 'Active lotes', activeLotes.value.length);
};

const openModal = (): void => {
  log('MensagensProgramadasLotePage', 'openModal', 'Opening modal via eventBus for new operation');
  eventBus.emit('modal.mensagens-programadas-lote.open', { editId: null });
};

const handleEdit = (id: Id): void => {
  log('MensagensProgramadasLotePage', 'handleEdit', 'Opening modal via eventBus for edit', id);
  eventBus.emit('modal.mensagens-programadas-lote.open', { editId: id });
};

const handleSaved = (): void => {
  log('MensagensProgramadasLotePage', 'handleSaved', 'Operation saved');
  eventBus.emit('mensagens-programadas-lote.refresh');
};

onMounted(() => {
  log('MensagensProgramadasLotePage', 'onMounted', 'Component mounted');
  eventBus.on('mensagens-programadas-lote.saved', handleSaved);
});

onBeforeUnmount(() => {
  log('MensagensProgramadasLotePage', 'onBeforeUnmount', 'Cleaning up event listeners');
  eventBus.off('mensagens-programadas-lote.saved', handleSaved);
});
</script>

<style scoped>
.mensagens-programadas-lote-page {
  padding: 0;
}

.page-header {
  display: flex;
  justify-content: flex-start;
  margin: 20px 0;
}

.active-progress-section {
  margin-top: 20px;
  padding: 16px;
  border: 1px solid var(--surface-border);
  border-radius: 8px;
  background: var(--surface-50);
}

.active-progress-title {
  margin: 0 0 12px 0;
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--text-color);
}

.active-progress-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 0;
  border-bottom: 1px solid var(--surface-border);
}

.active-progress-item:last-child {
  border-bottom: none;
}

.active-progress-label {
  font-size: 0.875rem;
  color: var(--text-color-secondary);
  min-width: 80px;
  flex-shrink: 0;
}

@media (max-width: 768px) {
  .page-header {
    flex-direction: column;
  }

  .page-header :deep(.p-button) {
    width: 100%;
  }

  .active-progress-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
  }
}
</style>
